import { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Timer, Search, Loader2, Trophy, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { apiError, SPORT_LABEL } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function Results() {
  const { user } = useAuth();
  const isSuper = user?.role === "SUPER_ADMIN";
  const canOperate = isSuper || (user?.permissions || []).includes("OPERATOR");
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [times, setTimes] = useState({});
  const [ranking, setRanking] = useState([]);
  const [published, setPublished] = useState(false);
  const [busy, setBusy] = useState(null);
  const inputRef = useRef(null);

  const loadRanking = useCallback(() => api.get("/staff/results").then((r) => setRanking(r.data.categories)).catch(() => {}), []);
  useEffect(() => {
    loadRanking();
    api.get("/public/event").then((r) => setPublished(!!r.data.event.results_published)).catch(() => {});
    inputRef.current?.focus();
  }, [loadRanking]);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    try {
      const { data } = await api.get("/registrations", { params: { q: q.trim(), status: "PAID" } });
      setList(data.registrations);
      if (!data.registrations.length) toast.info("Nenhuma inscrição paga encontrada");
    } catch (err) { toast.error(apiError(err)); }
  };

  const save = async (reg) => {
    setBusy(reg.id);
    try {
      const { data } = await api.patch(`/registrations/${reg.id}/result`, { finish_time: times[reg.id] || null });
      setList((l) => l.map((r) => (r.id === reg.id ? data : r)));
      toast.success(`Tempo salvo: ${data.bib_code}`);
      loadRanking();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  const togglePublish = async () => {
    try {
      await api.put("/admin/event", { results_published: !published });
      setPublished(!published);
      toast.success(!published ? "Resultados publicados no site!" : "Resultados ocultados do site");
    } catch (err) { toast.error(apiError(err)); }
  };

  const fmt = (s) => s == null ? "" : `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="space-y-6" data-testid="results-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-brand font-bold uppercase tracking-widest text-xs">Cronometragem</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase">Resultados da Prova</h1>
        </div>
        {isSuper && (
          <Button data-testid="results-publish-btn" onClick={togglePublish}
            className={published ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 font-bold" : "bg-white/5 border border-white/10 hover:bg-white/10 font-bold"}>
            {published ? <><Eye className="w-4 h-4 mr-2" /> Publicado no site</> : <><EyeOff className="w-4 h-4 mr-2" /> Publicar no site</>}
          </Button>
        )}
      </div>

      {canOperate && (
        <>
          <form onSubmit={search} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
              <Input ref={inputRef} data-testid="results-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Nº de peito ou nome" className="pl-9 bg-surface border-white/10 h-11 font-mono" />
            </div>
            <Button data-testid="results-search-btn" type="submit" className="bg-brand hover:bg-brand-hover font-bold uppercase h-11 px-6">Buscar</Button>
          </form>
          <div className="space-y-3">
            {list.map((reg) => (
              <div key={reg.id} data-testid={`result-row-${reg.bib_code}`} className="rounded-2xl bg-surface border border-white/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-mono font-bold text-lg text-neon tracking-widest">{reg.bib_code}</div>
                  <div className="font-semibold truncate">{reg.runner?.full_name}</div>
                  <div className="text-xs text-slate-400">{SPORT_LABEL[reg.category?.sport_type]} — {reg.category?.name}{reg.result_seconds != null && <span className="text-emerald-300"> • Tempo atual {fmt(reg.result_seconds)}</span>}</div>
                </div>
                <div className="flex gap-2">
                  <Input data-testid={`result-time-${reg.bib_code}`} placeholder="HH:MM:SS" value={times[reg.id] ?? fmt(reg.result_seconds)}
                    onChange={(e) => setTimes((t) => ({ ...t, [reg.id]: e.target.value }))} className="bg-ink border-white/10 font-mono w-36" />
                  <Button data-testid={`result-save-${reg.bib_code}`} onClick={() => save(reg)} disabled={busy === reg.id} className="bg-brand hover:bg-brand-hover font-bold">
                    {busy === reg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Timer className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="space-y-4" data-testid="results-ranking">
        {ranking.length === 0 && <p className="text-slate-500 text-sm">Nenhum tempo lançado ainda.</p>}
        {ranking.map((c) => (
          <div key={c.category_id} className="rounded-2xl bg-surface border border-white/10 overflow-hidden">
            <div className="px-4 py-2 bg-brand/15 border-b border-white/10 font-display uppercase font-bold flex items-center gap-2"><Trophy className="w-4 h-4 text-brand" /> {c.category}</div>
            <table className="w-full text-sm">
              <tbody>
                {c.results.map((r) => (
                  <tr key={r.bib_code} className="border-b border-white/5">
                    <td className="px-4 py-2 font-display font-black w-12">{r.position}º</td>
                    <td className="px-4 py-2 font-mono text-neon">{r.bib_code}</td>
                    <td className="px-4 py-2">{r.name}</td>
                    <td className="px-4 py-2 font-mono text-right">{r.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
